import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  DiffDown,
  DiffUp,
  Error,
  Feed,
  OfferCardBlock,
  OfferItem,
  OfferLink,
  PreviewImg,
  Price,
  PriceDiff,
  Subtext,
} from '../components';
import { ConditionalWrapper } from './ConditionalWrapper';
import { handleRefreshPage } from './utils/handleRefreshPage';
import { useHandleOfferClick } from './utils/useHandleOfferClick';
import { selectIsError, selectOfferHistoryData } from '../../../store/offerHistory/selectors';
import { EPriceDiff } from '../../../repositories/valuation-offer-history/entities/offer_history/HouseOfferHistoryDesktopSchema';
import { useActualColTitle } from './utils/useActualColTitle';

const priceDiffIcons = {
  [EPriceDiff.Decrease]: DiffDown,
  [EPriceDiff.Increase]: DiffUp,
  [EPriceDiff.NoChange]: undefined,
};

/**
 * Контейнер собирает ленту офферов
 * */
export const FeedContainer: React.FC = () => {
  const offerHistoryData = useSelector(selectOfferHistoryData);
  const offerHistoryError = useSelector(selectIsError);
  const actualColTitle = useActualColTitle();
  const handleOfferClick = useHandleOfferClick();

  if (offerHistoryError) {
    return <Error handleClick={handleRefreshPage} />;
  }

  if (!offerHistoryData) {
    return null;
  }

  const rows = offerHistoryData.offers.map(offer => {
    const handleOfferClickBound = handleOfferClick.bind(null, offer.id);

    const offerCardBlock = (
      <OfferCardBlock
        previewImage={
          <ConditionalWrapper
            shouldWrap={Boolean(offer.card.link)}
            Wrapper={OfferLink}
            wrapperProps={{
              onClick: handleOfferClickBound,
              link: offer.card.link as string,
            }}
          >
            <PreviewImg src={offer.card.img} width={68} height={68} />
          </ConditionalWrapper>
        }
        title={
          <ConditionalWrapper
            shouldWrap={Boolean(offer.card.link)}
            Wrapper={OfferLink}
            wrapperProps={{
              onClick: handleOfferClickBound,
              link: offer.card.link as string,
            }}
          >
            {offer.card.title}
          </ConditionalWrapper>
        }
        expoTime={offer.card.expoTime}
        onOfferClick={handleOfferClickBound}
      />
    );

    const offerStartBlock = (
      <React.Fragment>
        <Price>{offer.offerStart.price}</Price>
        <Subtext>{offer.offerStart.priceSqm}</Subtext>
        <Subtext>{offer.offerStart.date}</Subtext>
      </React.Fragment>
    );

    const DiffIcon = priceDiffIcons[offer.offerActual.priceDiff];

    const offerActualBlock = (
      <React.Fragment>
        <PriceDiff diff={DiffIcon && <DiffIcon />}>{offer.offerActual.price}</PriceDiff>
        <Subtext>{offer.offerActual.priceSqm}</Subtext>
        <Subtext>{offer.offerActual.date}</Subtext>
      </React.Fragment>
    );

    const offerValuationBlock = (
      <React.Fragment>
        <Price>{offer.offerValuation.price}</Price>
        <Subtext>{offer.offerValuation.priceSqm}</Subtext>
      </React.Fragment>
    );

    return (
      <OfferItem
        key={offer.id}
        offerCardBlock={offerCardBlock}
        offerStartBlock={offerStartBlock}
        offerActualBlock={offerActualBlock}
        offerValuationBlock={offerValuationBlock}
      />
    );
  });

  return <Feed rows={rows} actualColTitle={actualColTitle} />;
};

FeedContainer.displayName = 'FeedContainer';
